// ─────────────────────────────────────────────────────────────────────────────
// RETARGETING ENGINE — MediaPipe landmarks → GLB bone transforms
//
// MediaPipe normalized landmarks (0–1 per axis) are converted into world-space
// positions and then mapped onto the 4-bone armature used by the Blender
// processing pipeline (Hips / Spine / LeftShoulder / RightShoulder).
//
// World-space formula (spec): x = (nx - 0.5) * 4,  y = -(y - 0.5) * 4
// Scale formula (spec):       scale = shoulderDist * 5
// ─────────────────────────────────────────────────────────────────────────────

import * as THREE from "three";
import type { NormalizedLandmark } from "./pose";
import { POSE_IDX } from "./pose";
import { OneEuroLandmarkSmoother } from "./smoother";

// ── MediaPipe landmark index aliases ─────────────────────────────────────────
export const MP = {
  LEFT_SHOULDER:  POSE_IDX.LEFT_SHOULDER,   // 11
  RIGHT_SHOULDER: POSE_IDX.RIGHT_SHOULDER,  // 12
  LEFT_ELBOW:     POSE_IDX.LEFT_ELBOW,      // 13
  RIGHT_ELBOW:    POSE_IDX.RIGHT_ELBOW,     // 14
  LEFT_WRIST:     POSE_IDX.LEFT_WRIST,      // 15
  RIGHT_WRIST:    POSE_IDX.RIGHT_WRIST,     // 16
  LEFT_HIP:       POSE_IDX.LEFT_HIP,        // 23
  RIGHT_HIP:      POSE_IDX.RIGHT_HIP,       // 24
  LEFT_KNEE:      POSE_IDX.LEFT_KNEE,       // 25
  RIGHT_KNEE:     POSE_IDX.RIGHT_KNEE,      // 26
  LEFT_ANKLE:     27,
  RIGHT_ANKLE:    28,
} as const;

// ── Bone names — must match Blender armature exactly ─────────────────────────
export const BONE_NAMES = {
  Hips:           "Hips",
  Spine:          "Spine",
  LeftShoulder:   "LeftShoulder",
  RightShoulder:  "RightShoulder",
} as const;

// ── Bone→MediaPipe primary landmark mapping ───────────────────────────────────
export const BONE_TO_MEDIAPIPE: Record<string, number> = {
  [BONE_NAMES.Hips]:          -1,          // midpoint of hips (computed)
  [BONE_NAMES.Spine]:         -1,          // midpoint of shoulders+hips (computed)
  [BONE_NAMES.LeftShoulder]:  MP.LEFT_SHOULDER,
  [BONE_NAMES.RightShoulder]: MP.RIGHT_SHOULDER,
};

// ── World space conversion ───────────────────────────────────────────────────
export function toWorld(
  x: number,
  y: number,
  isMirrored: boolean
): THREE.Vector3 {
  const nx = isMirrored ? 1 - x : x;
  return new THREE.Vector3(
    (nx - 0.5) * 4,
    -(y - 0.5) * 4,
    0
  );
}

export function toWorldZ(
  x: number,
  y: number,
  z: number,
  isMirrored: boolean
): THREE.Vector3 {
  const nx = isMirrored ? 1 - x : x;
  return new THREE.Vector3(
    (nx - 0.5) * 4,
    -(y  - 0.5) * 4,
    z * -2
  );
}

// ── Body scale from shoulder span ────────────────────────────────────────────
export function getBodyScale(
  ls: NormalizedLandmark,
  rs: NormalizedLandmark
): number {
  const dx = ls.x - rs.x;
  const dy = ls.y - rs.y;
  return Math.sqrt(dx * dx + dy * dy) * 5;
}

// ── Per-frame pose data extracted from landmarks ──────────────────────────────
export interface RetargetPose {
  // World-space positions
  hipCenter:       THREE.Vector3;
  spineCenter:     THREE.Vector3;
  leftShoulder:    THREE.Vector3;
  rightShoulder:   THREE.Vector3;
  leftElbow:       THREE.Vector3;
  rightElbow:      THREE.Vector3;
  // Derived
  bodyScale:       number;
  shoulderAngle:   number;   // radians, tilt of shoulder line
  hipAngle:        number;
  torsoUp:         THREE.Vector3;
  torsoRight:      THREE.Vector3;
}

// ── Main retargeting class ───────────────────────────────────────────────────
export class RetargetEngine {
  private smoother: OneEuroLandmarkSmoother;

  // Bone references populated by setGLBBones()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private boneHips:          any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private boneSpine:         any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private boneLeftShoulder:  any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private boneRightShoulder: any = null;

  // Bone rest-pose inversions (to isolate delta rotation)
  private restInvHips         = new THREE.Quaternion();
  private restInvSpine        = new THREE.Quaternion();
  private restInvLeftShoulder  = new THREE.Quaternion();
  private restInvRightShoulder = new THREE.Quaternion();

  constructor(private isMirrored = true) {
    // 33 MediaPipe landmarks, minCutoff=1.5 for pose stability
    this.smoother = new OneEuroLandmarkSmoother(33, 1.5, 0.007);
  }

  // ── Bind to a loaded GLB scene ─────────────────────────────────────────────
  bindGLB(scene: THREE.Object3D) {
    this.boneHips = null;
    this.boneSpine = null;
    this.boneLeftShoulder = null;
    this.boneRightShoulder = null;

    scene.traverse(obj => {
      if (!(obj instanceof THREE.Bone)) return;
      switch (obj.name) {
        case BONE_NAMES.Hips:          this.boneHips          = obj; break;
        case BONE_NAMES.Spine:         this.boneSpine         = obj; break;
        case BONE_NAMES.LeftShoulder:  this.boneLeftShoulder  = obj; break;
        case BONE_NAMES.RightShoulder: this.boneRightShoulder = obj; break;
      }
    });

    // Cache rest-pose inverse quaternions
    if (this.boneHips)          this.restInvHips.copy(this.boneHips.quaternion).invert();
    if (this.boneSpine)         this.restInvSpine.copy(this.boneSpine.quaternion).invert();
    if (this.boneLeftShoulder)  this.restInvLeftShoulder.copy(this.boneLeftShoulder.quaternion).invert();
    if (this.boneRightShoulder) this.restInvRightShoulder.copy(this.boneRightShoulder.quaternion).invert();

    const found = [
      this.boneHips, this.boneSpine,
      this.boneLeftShoulder, this.boneRightShoulder
    ].filter(Boolean).length;

    console.log(`[Retarget] Bound ${found}/4 bones from GLB`);
    return found;
  }

  isBound(): boolean {
    return !!(this.boneHips && this.boneSpine &&
              this.boneLeftShoulder && this.boneRightShoulder);
  }

  // ── Extract pose from raw landmarks ──────────────────────────────────────
  extractPose(
    raw: NormalizedLandmark[],
    timestamp: number
  ): RetargetPose | null {
    const lms = this.smoother.smooth(raw, timestamp);

    const ls  = lms[MP.LEFT_SHOULDER];
    const rs  = lms[MP.RIGHT_SHOULDER];
    const lh  = lms[MP.LEFT_HIP];
    const rh  = lms[MP.RIGHT_HIP];
    const le  = lms[MP.LEFT_ELBOW];
    const re  = lms[MP.RIGHT_ELBOW];

    if (!ls || !rs || !lh || !rh) return null;
    if ((ls.visibility ?? 1) < 0.3 || (rs.visibility ?? 1) < 0.3) return null;

    const wLS  = toWorldZ(ls.x, ls.y, ls.z ?? 0, this.isMirrored);
    const wRS  = toWorldZ(rs.x, rs.y, rs.z ?? 0, this.isMirrored);
    const wLH  = toWorldZ(lh.x, lh.y, lh.z ?? 0, this.isMirrored);
    const wRH  = toWorldZ(rh.x, rh.y, rh.z ?? 0, this.isMirrored);
    const wLE  = le ? toWorldZ(le.x, le.y, le.z ?? 0, this.isMirrored) : wLS.clone();
    const wRE  = re ? toWorldZ(re.x, re.y, re.z ?? 0, this.isMirrored) : wRS.clone();

    const hipCenter   = wLH.clone().add(wRH).multiplyScalar(0.5);
    const shCenter    = wLS.clone().add(wRS).multiplyScalar(0.5);
    const spineCenter = hipCenter.clone().lerp(shCenter, 0.5);

    // Torso orientation basis vectors
    const torsoRight = wRS.clone().sub(wLS).normalize();
    const torsoUp    = shCenter.clone().sub(hipCenter).normalize();

    const shoulderAngle = Math.atan2(wRS.y - wLS.y, wRS.x - wLS.x);
    const hipAngle      = Math.atan2(wRH.y - wLH.y, wRH.x - wLH.x);

    return {
      hipCenter,
      spineCenter,
      leftShoulder:  wLS,
      rightShoulder: wRS,
      leftElbow:     wLE,
      rightElbow:    wRE,
      bodyScale:     getBodyScale(ls, rs),
      shoulderAngle,
      hipAngle,
      torsoUp,
      torsoRight,
    };
  }

  // ── Apply pose to GLB skeleton ─────────────────────────────────────────────
  // Blend: skeletalWeight drives bone transforms, remainder is left for physics
  applyPose(
    pose: RetargetPose,
    scene: THREE.Object3D,
    skeletalWeight = 0.7   // spec: 70% skeletal, 30% cloth physics
  ) {
    // ── Position + scale the garment root ──────────────────────────────────
    scene.position.copy(pose.hipCenter);
    scene.scale.setScalar(pose.bodyScale);

    // ── Spine tilt from hip angle ───────────────────────────────────────────
    if (this.boneSpine) {
      const tilt = (pose.shoulderAngle + pose.hipAngle) * 0.5;
      const target = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(0, 0, 1), tilt
      );
      this.boneSpine.quaternion.slerp(target, skeletalWeight);
    }

    // ── Hips roll ───────────────────────────────────────────────────────────
    if (this.boneHips) {
      const target = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(0, 0, 1), pose.hipAngle
      );
      this.boneHips.quaternion.slerp(target, skeletalWeight);
    }

    // ── Left shoulder: rotate toward elbow ─────────────────────────────────
    if (this.boneLeftShoulder) {
      const dir = pose.leftElbow.clone().sub(pose.leftShoulder).normalize();
      const angle = Math.atan2(dir.y, dir.x);
      const target = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(0, 0, 1), angle
      );
      this.boneLeftShoulder.quaternion.slerp(target, skeletalWeight);
    }

    // ── Right shoulder: rotate toward elbow ────────────────────────────────
    if (this.boneRightShoulder) {
      const dir = pose.rightElbow.clone().sub(pose.rightShoulder).normalize();
      const angle = Math.atan2(dir.y, dir.x);
      const target = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(0, 0, 1), angle
      );
      this.boneRightShoulder.quaternion.slerp(target, skeletalWeight);
    }

    scene.updateMatrixWorld(true);
  }

  setMirrored(m: boolean) {
    this.isMirrored = m;
  }

  reset() {
    this.smoother.reset();
  }
}
